import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function TermsAndConditions() {
  const navigate = useNavigate();

  const contractStyle = {
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: '11px',
    lineHeight: '1.4',
    color: '#000',
    textAlign: 'justify'
  };

  const headingStyle = {
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginTop: '16px',
    marginBottom: '8px',
    textAlign: 'left'
  };

  const titleStyle = {
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: '16px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    textAlign: 'center',
    marginBottom: '8px'
  };

  return (
    <Container maxWidth="md" sx={{ py: 4, minHeight: '100vh', bgcolor: '#fff' }}>
      <Box sx={{ bgcolor: '#fff', p: 4, border: '1px solid #000' }}>
        <Typography sx={titleStyle}>
          TERMS AND CONDITIONS
        </Typography>

        <Typography sx={{ ...contractStyle, textAlign: 'center', mb: 3 }}>
          Last Updated: December 20, 2025
        </Typography>

        <Typography sx={{ ...contractStyle, mb: 2 }}>
          Welcome to JURIS CODEX, an AI-powered legal case management system. 
          By accessing or using our software and services, you agree to be bound by these Terms and 
          Conditions. Please read them carefully before using the platform.
        </Typography>

        {/* Section 1: Acceptance of Terms */}
        <Typography sx={headingStyle}>
          1. ACCEPTANCE OF TERMS
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          By registering for, accessing, or using Juris Codex, you acknowledge that you have read, 
          understood, and agree to be bound by these Terms and Conditions. If you do not agree with 
          these terms, you must not use the software.
        </Typography>

        {/* Section 2: Use "As Is" */}
        <Typography sx={headingStyle}>
          2. SOFTWARE PROVIDED "AS IS" - NO WARRANTIES
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 1 }}>
          JURIS CODEX IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. THE DEVELOPERS 
          AND OPERATORS OF JURIS CODEX MAKE NO WARRANTIES, EXPRESS OR IMPLIED, REGARDING THE SOFTWARE'S 
          FUNCTIONALITY, ACCURACY, RELIABILITY, OR SUITABILITY FOR ANY PARTICULAR PURPOSE.
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          You explicitly acknowledge and agree that: (a) the software may contain errors, bugs, or inaccuracies; 
          (b) AI-generated outputs are not guaranteed to be legally accurate or complete; (c) the software should 
          not be relied upon as a substitute for professional legal advice; and (d) service availability may be 
          interrupted or discontinued at any time without notice or liability.
        </Typography>

        {/* Section 3: No Liability */}
        <Typography sx={headingStyle}>
          3. LIMITATION OF LIABILITY AND DISCLAIMER
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 1 }}>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE DEVELOPERS, OWNERS, OPERATORS, AND 
          AFFILIATES OF JURIS CODEX SHALL NOT BE LIABLE FOR ANY: (a) DIRECT, INDIRECT, INCIDENTAL, SPECIAL, 
          CONSEQUENTIAL, OR PUNITIVE DAMAGES; (b) LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS OPPORTUNITIES; 
          (c) ERRORS, OMISSIONS, OR INACCURACIES IN THE SOFTWARE OR AI-GENERATED CONTENT; (d) UNAUTHORIZED 
          ACCESS TO OR USE OF YOUR DATA; (e) ANY LEGAL CONSEQUENCES ARISING FROM YOUR USE OF THE SOFTWARE; 
          (f) ANY DAMAGES ARISING FROM THE USE OR INABILITY TO USE THE SOFTWARE.
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          This limitation applies regardless of the legal theory on which the claim is based, whether 
          in contract, tort, negligence, strict liability, or otherwise, and even if the developers have 
          been advised of the possibility of such damages.
        </Typography>

        {/* Section 4: Data Sharing and Privacy */}
        <Typography sx={headingStyle}>
          4. DATA SHARING AND CONFIDENTIALITY ACKNOWLEDGMENT
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 1 }}>
          BY USING JURIS CODEX, YOU EXPLICITLY ACKNOWLEDGE AND AGREE THAT: (a) You are voluntarily sharing 
          your data, legal cases, documents, and other information (which may be confidential) with the developers 
          and operators of Juris Codex; (b) The developers may access, store, and process this information to 
          provide and improve the services; (c) You have obtained all necessary consents and permissions to share 
          such data; (d) You are solely responsible for ensuring compliance with client confidentiality obligations 
          and data protection laws; (e) The developers will make reasonable efforts to protect your data, but cannot 
          guarantee absolute security against unauthorized access, disclosure, or loss.
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          WARNING: Do not upload or share information that you are legally prohibited from disclosing or that 
          violates attorney-client privilege without proper authorization. You assume all risks associated with 
          sharing confidential or sensitive information through this platform.
        </Typography>

        {/* Section 5: User Responsibilities */}
        <Typography sx={headingStyle}>
          5. USER RESPONSIBILITIES
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          You agree to: (a) use the software in compliance with all applicable laws and regulations; (b) verify 
          all AI-generated content independently before using it in legal matters; (c) maintain the confidentiality 
          of your account credentials; (d) not use the software for any unlawful, fraudulent, or harmful purposes; 
          (e) not attempt to reverse engineer, decompile, or hack the software; (f) not interfere with or disrupt 
          the integrity or performance of the software.
        </Typography>

        {/* Section 6: Intellectual Property */}
        <Typography sx={headingStyle}>
          6. INTELLECTUAL PROPERTY
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          All rights, title, and interest in and to Juris Codex, including all software, algorithms, 
          designs, and content (excluding user-uploaded content), are and will remain the exclusive 
          property of the developers and operators of Juris Codex. You are granted a limited, non-exclusive, 
          non-transferable, revocable license to use the software solely for your internal business purposes.
        </Typography>

        {/* Section 7: Termination */}
        <Typography sx={headingStyle}>
          7. TERMINATION
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          The developers reserve the right to suspend or terminate your access to Juris Codex at any 
          time, with or without cause, and with or without notice. Upon termination, your right to 
          use the software will immediately cease, and you must destroy all copies of materials obtained 
          from the platform.
        </Typography>

        {/* Section 8: Indemnification */}
        <Typography sx={headingStyle}>
          8. INDEMNIFICATION
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          You agree to indemnify, defend, and hold harmless the developers, operators, affiliates, officers, 
          directors, employees, and agents of Juris Codex from and against any and all claims, liabilities, 
          damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or 
          related to: (a) your use or misuse of the software; (b) your violation of these Terms and Conditions; 
          (c) your violation of any third-party rights, including confidentiality obligations, privacy rights, 
          or intellectual property rights; (d) any content or data you upload or share through the software; 
          (e) any breach of your representations and warranties herein.
        </Typography>

        {/* Section 9: Governing Law and Jurisdiction */}
        <Typography sx={headingStyle}>
          9. GOVERNING LAW AND DISPUTE RESOLUTION
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          These Terms and Conditions shall be governed by and construed in accordance with the laws of India, 
          without regard to its conflict of law provisions. For any disputes, claims, or controversies arising 
          out of or relating to these Terms and Conditions or your use of Juris Codex, the courts of Gurgaon, 
          Haryana, India shall have exclusive jurisdiction. You irrevocably submit to the jurisdiction of the 
          courts located in Gurgaon, Haryana, and waive any objection to the venue or inconvenience of such courts.
        </Typography>

        {/* Section 10: Arbitration */}
        <Typography sx={headingStyle}>
          10. ARBITRATION
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          Any dispute arising out of or in connection with these Terms and Conditions, including any 
          question regarding its existence, validity, or termination, shall be referred to and finally 
          resolved by arbitration in accordance with the Arbitration and Conciliation Act, 1996. The seat 
          and venue of arbitration shall be Gurgaon, Haryana, India. The tribunal shall consist of a sole 
          arbitrator appointed by mutual agreement of the parties or, failing such agreement, appointed in 
          accordance with said Act. The language of arbitration shall be English, and the decision of the 
          arbitrator(s) shall be final and binding on both parties. Each party shall bear its own costs of 
          arbitration unless otherwise determined by the arbitrator.
        </Typography>

        {/* Section 11: Changes to Terms */}
        <Typography sx={headingStyle}>
          11. CHANGES TO TERMS AND CONDITIONS
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          The developers reserve the right to modify these Terms and Conditions at any time without prior 
          notice. Changes will be effective immediately upon posting to the platform or upon such later date 
          as may be specified. Your continued use of Juris Codex after changes are posted constitutes your 
          acceptance of the modified terms. It is your responsibility to review these terms periodically.
        </Typography>

        {/* Section 12: Severability */}
        <Typography sx={headingStyle}>
          12. SEVERABILITY
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          If any provision of these Terms and Conditions is found to be invalid, illegal, or 
          unenforceable by a court of competent jurisdiction, the remaining provisions shall continue 
          in full force and effect. The invalid or unenforceable provision shall be deemed modified to 
          the extent necessary to make it valid and enforceable.
        </Typography>

        {/* Section 13: Entire Agreement */}
        <Typography sx={headingStyle}>
          13. ENTIRE AGREEMENT
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          These Terms and Conditions constitute the entire agreement between you and Juris Codex 
          regarding your use of the software and supersede all prior agreements, understandings, 
          negotiations, and discussions, whether oral or written, between the parties relating to 
          the subject matter hereof.
        </Typography>

        {/* Section 14: Waiver */}
        <Typography sx={headingStyle}>
          14. WAIVER
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 2 }}>
          No waiver of any term or condition of these Terms and Conditions shall be deemed a further or 
          continuing waiver of such term or condition or any other term or condition. The failure of either 
          party to assert any right or provision under these Terms and Conditions shall not constitute a 
          waiver of such right or provision.
        </Typography>

        {/* Section 15: Contact */}
        <Typography sx={headingStyle}>
          15. CONTACT INFORMATION
        </Typography>
        <Typography sx={{ ...contractStyle, mb: 3 }}>
          If you have any questions about these Terms and Conditions, please contact the developers 
          of Juris Codex through the platform's support channels.
        </Typography>

        {/* Final Acknowledgment in Fine Print */}
        <Box sx={{ borderTop: '1px solid #000', pt: 2, mt: 3 }}>
          <Typography sx={{ ...contractStyle, fontWeight: 'bold', mb: 1 }}>
            ACKNOWLEDGMENT OF ACCEPTANCE
          </Typography>
          <Typography sx={{ ...contractStyle, mb: 2 }}>
            BY CLICKING "I UNDERSTAND AND ACCEPT" OR BY ACCESSING OR USING JURIS CODEX, YOU ACKNOWLEDGE 
            THAT: (1) YOU HAVE READ AND UNDERSTOOD THESE TERMS AND CONDITIONS IN THEIR ENTIRETY; (2) YOU 
            AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS; (3) THE SOFTWARE IS PROVIDED "AS IS" WITH 
            ABSOLUTELY NO WARRANTIES OF ANY KIND; (4) THE DEVELOPERS HAVE NO LIABILITY WHATSOEVER FOR ANY 
            DAMAGES, LOSSES, OR LEGAL CONSEQUENCES ARISING FROM YOUR USE OF THE SOFTWARE; (5) YOU ARE 
            VOLUNTARILY SHARING POTENTIALLY CONFIDENTIAL AND PRIVILEGED INFORMATION WITH THE DEVELOPERS; 
            (6) ALL DISPUTES SHALL BE SUBJECT TO ARBITRATION IN GURGAON, HARYANA, INDIA; AND (7) YOU HAVE 
            THE LEGAL AUTHORITY TO ENTER INTO THIS AGREEMENT.
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '1px solid #ccc' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/login')}
            sx={{ textTransform: 'none', color: '#000', borderColor: '#000' }}
          >
            Back to Login
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{ textTransform: 'none', bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
          >
            I Understand and Accept
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default TermsAndConditions;
